import { useRef, useState, type FormEvent } from 'react';
import useSeo from '@/hooks/useSeo';
import { faqs, site } from '@/mocks/site';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const FORM_ID = 'dah8n33oh653ivfvnog0';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah8n33oh653ivfvnog0';

const topics = ['提供线索', '纠错与补充', '内容合作咨询', '其他事项'];

export default function Contact() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  useSeo({
    title: '联系我们 - 爆料看点｜线索投稿与纠错补充',
    description:
      '联系爆料看点：提供新闻线索、补充时间线节点、纠错或咨询合作。请附上素材出现的时间与来源，我们会在工作日完成交叉核对并回复。',
    keywords: '联系爆料看点,线索投稿,爆料背后细节,时间线关键节点,纠错补充',
    path: '/contact',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系爆料看点',
        description: '提供线索、纠错补充与内容合作咨询的联系方式。',
        inLanguage: 'zh-CN',
        isPartOf: { '@type': 'WebSite', name: site.name, url: 'https://baoliaokandian.site/' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.name,
        url: 'https://baoliaokandian.site/',
        email: site.email,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'editorial',
          email: site.email,
          availableLanguage: ['zh-CN'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaokandian.site/' },
          { '@type': 'ListItem', position: 2, name: '联系我们', item: 'https://baoliaokandian.site/contact' },
        ],
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }
    formData.delete('website_alt');

    const name = String(formData.get('name') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const topic = String(formData.get('topic') ?? '').trim();
    const message = String(formData.get('message') ?? '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请把姓名、邮箱和线索描述填写完整后再提交。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('线索描述不能超过 500 个字符，请精简后再提交。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('邮箱格式看起来不太对，请检查后再提交。');
      return;
    }

    const params = new URLSearchParams();
    params.append('name', name);
    params.append('email', email);
    params.append('topic', topic);
    params.append('message', message);

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
        setMessageLength(0);
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交未通过校验，请检查内容后重试。' : serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交没有成功。请检查网络后重试，或直接发邮件给我们。');
    }
  };

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-8 md:px-8">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '联系我们' }]} />
        </div>
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <span className="font-label text-xs tracking-[0.3em] text-primary-600">CONTACT / 联系我们</span>
          <h1 className="mt-3 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            有线索想补充？交给我们排一遍
          </h1>
          <p className="mt-5 max-w-2xl font-body text-base leading-relaxed text-foreground-700">
            一段录屏、一张截图、一句被忽略的发言，都可能补上缺失的那一格。请尽量附上素材出现的时间与来源。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <form
              ref={formRef}
              id={FORM_ID}
              data-readdy-form="true"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
            >
              <h2 className="font-heading text-xl font-bold text-foreground-950">线索投稿表单</h2>
              <p className="mt-2 font-body text-sm text-foreground-600">
                带 <span className="text-primary-600">*</span> 的为必填项，我们会在 {site.workingHours} 内回复。
              </p>

              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="font-label text-sm text-foreground-800">
                    你的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="例如：许屿"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-body text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="font-label text-sm text-foreground-800">
                    邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="用于接收回复"
                    className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-body text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label htmlFor="topic" className="font-label text-sm text-foreground-800">
                  事项类型
                </label>
                <select
                  id="topic"
                  name="topic"
                  defaultValue={topics[0]}
                  className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-body text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  {topics.map((topic) => (
                    <option key={topic} value={topic}>
                      {topic}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <label htmlFor="message" className="font-label text-sm text-foreground-800">
                  线索描述 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  onChange={(event) => setMessageLength(event.target.value.length)}
                  placeholder="请描述素材内容、出现时间与来源渠道，越具体越好。"
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-body text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
                />
                <span className="self-end font-label text-xs text-foreground-500">{messageLength} / 500</span>
              </div>

              <div className="alt-contact-field" aria-hidden="true">
                <label htmlFor="website_alt">请勿填写</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
                {status === 'submitting' ? '正在提交…' : '提交线索'}
              </button>

              {status === 'success' && (
                <p className="mt-4 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 font-body text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                  已收到，感谢补充。我们会在工作日完成核对，并通过你留下的邮箱回复。
                </p>
              )}
              {status === 'error' && formError && (
                <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 font-body text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5" />
                  {formError}
                </p>
              )}
            </form>
          </div>

          <aside className="space-y-5 lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">直接联系</h2>
              <ul className="mt-5 space-y-4 font-body text-sm text-foreground-700">
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line mt-0.5 text-primary-500" />
                  <span>
                    投稿邮箱
                    <a
                      href={`mailto:${site.email}`}
                      className="mt-1 block break-all text-primary-600 hover:text-primary-700"
                      rel="nofollow"
                    >
                      {site.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-time-line mt-0.5 text-primary-500" />
                  <span>
                    响应时间
                    <span className="mt-1 block text-foreground-800">{site.workingHours}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line mt-0.5 text-primary-500" />
                  <span>
                    素材处理
                    <span className="mt-1 block text-foreground-600">
                      未经你同意，不会公开你的个人信息，仅用于线索核对。
                    </span>
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">更快提交的小建议</h2>
              <ol className="mt-5 space-y-3 font-body text-sm text-foreground-700">
                <li className="flex gap-3">
                  <span className="font-heading text-primary-500">01</span>
                  尽量提供原始素材，而不是二次剪辑版本。
                </li>
                <li className="flex gap-3">
                  <span className="font-heading text-primary-500">02</span>
                  写清楚素材出现的大致时间与渠道。
                </li>
                <li className="flex gap-3">
                  <span className="font-heading text-primary-500">03</span>
                  一句话说明你认为它对应哪条脉络。
                </li>
              </ol>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">提交前，先看看这些</h2>
          <ul className="mt-8 divide-y divide-background-200 border-y border-background-200">
            {faqs.map((item) => (
              <li key={item.q} className="grid grid-cols-1 gap-3 py-6 md:grid-cols-12">
                <h3 className="font-heading text-base font-semibold text-foreground-950 md:col-span-4">{item.q}</h3>
                <p className="font-body text-sm leading-relaxed text-foreground-600 md:col-span-8">{item.a}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}